<template>
  <div class="d-record__time"
       :style="[textColor]"
       :class="{ 'd-inline': elWidth < 200 }">
    <span class="align-middle">{{ time }}</span>
    <span class="d-inline-block align-middle ml-2 position-relative"
          style="top: -1px;"
          v-if="patientNotes && showClientTags"
          v-b-tooltip.noninteractive.hover
          :title="patientNotes">
              <WarnSvg/>
            </span>
    <div v-if="patient && patient.age && patient.age < 18"
         v-b-tooltip.noninteractive.hover
         style="top: -1px;"
         :title="$t('child_age')"
         class="d-inline-block align-middle ml-1 position-relative">
      <ChildSvg class="child-svg"/>
    </div>
    <div v-if="patient && patient.debt_calculated && patient.debt_calculated < 0"
         v-b-tooltip.noninteractive.hover
         style="top: -1px;"
         :title="$t('debtor')"
         class="d-inline-block align-middle ml-1 position-relative">
      <DebtSvg class="debtor-svg"/>
    </div>
    <div class="d-inline-block align-middle ml-1 position-relative"
         style="top: -0.5px"
         v-b-tooltip.noninteractive.hover
         :title="$t('new_patient')"
         v-if="firstVisitPatient">
      <OneSvg class="svg-first-visit"/>
    </div>
    <div v-if="curator"
         class="d-inline-block align-middle ml-1 position-relative"
         style="top: -1px;"
         v-b-tooltip.noninteractive.hover
         :title="`${$t('curator')}: ${curator.full_name}`">
      <span :style="{ color: curator.color }">K</span>
    </div>
    <div class="d-inline-block align-middle ml-1 position-relative"
         v-if="firstVisit"
         :class="{ 'text-danger': firstVisit }"
         style="font-size:10px"
         v-b-tooltip.noninteractive.hover
         :title="firstVisit ? $t('first_visit') : $t('return_visit')">
      {{ firstVisit ? '1В' : 'ПВ' }}
    </div>
    <div v-if="record.tags && record.tags.length"
         class="d-record__services d-inline-flex">
      <div class="d-record__tag"
           v-b-tooltip.noninteractive.hover
           v-for="(item, iind) in record.tags"
           :key="`record-tag-${record.id}-${iind}`"
           :alt="item.title"
           :title="item.title"
           :style="`border-bottom-color:${item.color};`">
      </div>
    </div>
    <div v-if="isOnlineBooking"
         class="d-inline-block ml-1 requires-confirmation small text-danger">{{ $t('online_booking') }} - <span class="text-lowercase">{{ $t('requires_attention') }}</span></div>
    <div v-if="record.isClosed"
         class="d-inline-block align-middle ml-1 position-relative"
         v-b-tooltip.noninteractive.hover
         :title="$t('closed')"
         style="top: -1px;">
      <LockSvg class="lock-svg"/>
    </div>
    <div v-if="showServiceColor && record.planned_services && record.planned_services.length"
         class="d-record__services d-inline-flex gap-2 ml-1">
      <template v-for="(item, iind) in record.planned_services">
        <div class="d-record__service generic-bubble"
             v-b-tooltip.noninteractive.hover
             v-if="item.category"
             :key="`plan-circle-${record.id}-${iind}`"
             :alt="item.name"
             :title="item.name"
             :style="`background-color:${item.category.color}; border: 1px solid ${item.category.color == '#FFFFFF' ? '#ddd' : 'transparent'};`">
        </div>
      </template>
    </div>
    <!--          </div>-->
    <div class="small descr order-2" v-if="record.description">{{ record.description }}</div>
  </div>
</template>

<script setup>
import WarnSvg from '@/assets/svg-vue/calendar/record_warn.svg'
import ChildSvg from '@/assets/svg-vue/calendar/child.svg'
import DebtSvg from '@/assets/svg-vue/calendar/debt.svg'
import OneSvg from '@/assets/svg-vue/calendar/one.svg'
import LockSvg from '@/assets/svg-vue/general/lock.svg'

import { computed } from "vue"
import {useStore} from "vuex"
import moment from 'moment/moment'

const { record } = defineProps({
  record: {
    type: Object,
    required: true
  },
  textColor: {
    type: Object,
    required: true
  },
  elWidth: {
    type: Number,
    default: 200
  }
})

const store = useStore()

const patient = computed(() => record.patient || {})
const showServiceColor = computed(() => store.state.calendar.showServiceColor)
const showClientTags = computed(() => store.state.calendar.showClientTags)
const curators = computed(() => store.state.auth.curators)
const isOnlineBooking = computed(() => record?.record_type === 'online')
const firstVisit = computed(() => !!record.first_visit)

const time = computed(() => {
  const from = moment(record.start).format('HH:mm')
  const to = moment(record.end).format('HH:mm')
  return `${from} - ${to}`
})
const firstVisitPatient = computed(() => {
  if(!patient.value) return false
  return patient.value.status_id !== 2
})
const patientNotes = computed(() => {
  if(!patient.value || !patient.value.client_notes) return ''
  let notes = patient.value.client_notes.filter(el => el.type)
  notes = notes.map(({name, description}) => `${name}: ${description}`)
  return notes.join(', ')
})
const curator = computed(() => {
  if(!patient.value?.curator_id) return null
  return curators.value.find(el => el.id === patient.value.curator_id)
})
</script>

<style scoped lang="scss">
.d-record__time {
  font-size: 12px;
  color: #87939E;
  word-break: break-word;
  flex-shrink: 0;
}
.d-record__tag {
  width: 0;
  height: 0;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-bottom: 8px solid;
  margin-right: 3px;
}
.child-svg {
  width: 9px;
  height: 10px;
  fill: $blue-color-darker;
}
.svg-first-visit {
  width: 14px;
  height: 14px;
}
.debtor-svg {
  width: 11px;
  height: 11px;
  fill: $danger-red;
}
.lock-svg {
  width: 11px;
  height: 11px;
  fill: $gray-text;
}
@media screen and (max-width: 768px) {
  .d-record__time {
    font-size: 10px;
  }
}
</style>
